import './poultryDesign.css';
import Button from '../shared/Button.js';
// ============================================================================
// src/broiler/BroilerListView.tsx — hub + router
// ----------------------------------------------------------------------------
// Hub is a navigation-only list of broiler batches. Row/card clicks navigate
// to /broiler/batches/<encoded name>, where BroilerBatchPage mounts BatchForm
// in embedded mode. Comments + collapsed Activity now live on the record
// page via RecordCollaborationSection. broiler.batch identity stays as
// batch.name to preserve the existing Activity entityId contract.
//
// THE HUB READS ITS COMPLETE ROUTE MODEL ITSELF. broiler.batches.hub owns the
// repaired ppp-v4 batch directory, complete poultry_dailys history, and
// ppp-feed-costs-v1. BatchesContext remains only a mirror for App's retained
// form/write closures; it is never read as this route's rendered data source.
// ============================================================================
import React from 'react';
import type {CSSProperties} from 'react';
import {useAppLocation as useLocation, useAppNavigate as useNavigate} from '../app/navigation/index.js';
import {sb} from '../lib/supabase.js';
import {fmtDate as fmt, fmtDate as fmtS} from '../lib/dateUtils.js';
import {S} from '../lib/styles.js';
import {getProgramColor} from '../lib/programColors.js';
import {
  calcTimeline,
  calcPoultryStatus,
  calcBroilerStatsFromDailys,
  getBatchDisplayColor,
  breedLabel,
} from '../lib/broiler.js';
import type {BroilerBatch, BroilerDailyRow} from '../lib/broiler.js';
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
import {GroupDot, StatusChipRow} from '../shared/GroupPillPicker.js';
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
import Badge from '../shared/Badge.jsx';
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
import StatusText from '../shared/StatusText.jsx';
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
import DataTable from '../shared/DataTable.jsx';
import {recordSeqNavOptions, labeledSeqItems} from '../lib/recordSequence.js';
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
import InlineNotice from '../shared/InlineNotice.jsx';
import type {Notice} from '../shared/InlineNotice.jsx';
import {csvFilename, downloadCsv, rowsToCsv} from '../lib/csvExport.js';
import {printRows} from '../lib/printExport.js';
import {buildBroilerBatchExportColumns} from '../lib/operationalExportColumns.js';
import {processingStatusLabel} from '../lib/processingStatusDisplay.js';
import {usePersistentViewState} from '../lib/usePersistentViewState.js';
import {createSavedView, updateSavedView, deleteSavedView, buildViewState} from '../lib/savedViewsApi.js';
import {savedViewsErrorMessage, useSavedViewsData} from '../shared/savedViewsData.js';
import {
  BROILER_BATCH_DEFAULT_SORT,
  broilerSortForStatusChip,
  buildBroilerBatchPredicate,
  buildBroilerBatchComparator,
  broilerBreedFilterOptions,
  broilerDistinctFieldValues,
} from '../lib/broilerBatchFilters.js';
import type {BroilerBatchContext, BroilerBatchFilters} from '../lib/broilerBatchFilters.js';
import {useAuth} from '../contexts/AuthContext.jsx';
import type {AuthIdentity} from '../contexts/AuthContext.jsx';
import {useBatches} from '../contexts/BatchesContext.jsx';
import {useConfirm} from '../contexts/ConfirmContext.jsx';
import {RESOURCE} from '../app/data/routeDataCatalog.js';
import type {BroilerBatchesHubData} from '../app/data/routeDataCatalog.js';
import {useRouteResource} from '../app/data/useRouteResource.js';
import BroilerBatchPage from './BroilerBatchPage.tsx';
import Header from '../shared/Header.tsx';

const BROILER_BATCHES_SURFACE_KEY = 'broiler.batches';

// Sort dropdown options (single active sort rule). Mirrors the hub's sort keys
// from broilerBatchFilters.js as labeled key:dir pairs. Default is processed
// newest-first (processing date descending).
const BROILER_SORT_OPTIONS = [
  {value: 'processingDate:desc', key: 'processingDate', dir: 'desc', label: 'Process date (newest)'},
  {value: 'processingDate:asc', key: 'processingDate', dir: 'asc', label: 'Process date (oldest)'},
  {value: 'batchName:asc', key: 'batchName', dir: 'asc', label: 'Name ↑'},
  {value: 'batchName:desc', key: 'batchName', dir: 'desc', label: 'Name ↓'},
  {value: 'status:asc', key: 'status', dir: 'asc', label: 'Status (planned first)'},
  {value: 'status:desc', key: 'status', dir: 'desc', label: 'Status (complete first)'},
  {value: 'startDate:asc', key: 'startDate', dir: 'asc', label: 'Hatch date (oldest)'},
  {value: 'startDate:desc', key: 'startDate', dir: 'desc', label: 'Hatch date (newest)'},
  {value: 'birdCount:desc', key: 'birdCount', dir: 'desc', label: 'Birds ↓'},
  {value: 'birdCount:asc', key: 'birdCount', dir: 'asc', label: 'Birds ↑'},
  {value: 'lbsProduced:desc', key: 'lbsProduced', dir: 'desc', label: 'Feed lbs ↓'},
  {value: 'lbsProduced:asc', key: 'lbsProduced', dir: 'asc', label: 'Feed lbs ↑'},
];

// The lifecycle slices the status chips offer, in the mock's order. `statuses`
// holds the EFFECTIVE statuses calcPoultryStatus hands back, so a chip, the
// `status` filter it writes and the Badge each row renders all agree. Statuses
// toggle independently; `all` selects every status. The owner's note supersedes
// the three-section design: one table, and it opens on In Process.
const BROILER_STATUS_CHIPS = [
  {key: 'all', label: 'All', statuses: null},
  {key: 'planned', label: 'Planned', statuses: ['planned']},
  {key: 'in_process', label: 'In Process', statuses: ['active']},
  {key: 'complete', label: 'Complete', statuses: ['processed']},
] as const;
const BROILER_DEFAULT_STATUS_CHIP = 'in_process';
const BROILER_DEFAULT_FILTERS: Record<string, unknown> = {
  status: [...(BROILER_STATUS_CHIPS.find((c) => c.key === BROILER_DEFAULT_STATUS_CHIP)?.statuses ?? [])],
};

// Toggleable data columns for the batches table (the column picker).
// `name` (Batch) and the row chevron are always shown; these are the columns the
// operator can hide/show, and the choice is stored in saved views. Defaults are
// the mock's ten: Batch · Breed/Hatchery · Hatch · Process · Birds · To
// Processor · Mortality · Total Feed · Cost / Bird · Status, which fit 1180px
// without horizontal scroll.
const BROILER_BATCH_COLUMNS = [
  // Breed carries hatchery as its gray sub-line (the mock's "Breed · Hatchery"
  // cell), so there is no separate hatchery column to toggle.
  {key: 'breed', label: 'Breed · Hatchery', default: true},
  {key: 'brooder', label: 'Brooder', default: false},
  {key: 'schooner', label: 'Schooner', default: false},
  {key: 'hatchDate', label: 'Hatch', default: true},
  {key: 'brooderPeriod', label: 'Brooder Period', default: false},
  {key: 'schoonerPeriod', label: 'Schooner Period', default: false},
  {key: 'processingDate', label: 'Process', default: true},
  {key: 'timeOnFarm', label: 'Time on Farm', default: false},
  {key: 'birdCount', label: 'Birds Ordered', default: true},
  {key: 'birdsArrived', label: 'Birds Arrived', default: false},
  {key: 'toProcessor', label: 'To Processor', default: true},
  {key: 'mortality', label: 'Mortality', default: true},
  {key: 'mortalityPct', label: 'Mortality %', default: false},
  {key: 'chickCost', label: 'Chick Cost', default: false},
  {key: 'starterFeed', label: 'Starter Feed', default: false},
  {key: 'growerFeed', label: 'Grower Feed', default: false},
  {key: 'totalFeed', label: 'Total Feed', default: true},
  {key: 'feedPerBird', label: 'Feed / Bird', default: false},
  {key: 'week4Lbs', label: '4-wk Wt', default: false},
  {key: 'week6Lbs', label: '6-wk Wt', default: false},
  {key: 'avgBreast', label: 'Avg Breast', default: false},
  {key: 'avgThigh', label: 'Avg Thigh', default: false},
  {key: 'avgDressed', label: 'Avg Whole', default: false},
  {key: 'totalMeat', label: 'Total Meat', default: false},
  {key: 'processingCost', label: 'Processing Cost', default: false},
  {key: 'processingPerBird', label: 'Processing / Bird', default: false},
  {key: 'totalCost', label: 'Total Cost', default: false},
  {key: 'perBird', label: 'Cost / Bird', default: true},
  {key: 'status', label: 'Status', default: true},
];
const BROILER_DEFAULT_COLUMN_KEYS = BROILER_BATCH_COLUMNS.filter((c) => c.default).map((c) => c.key);

// CSV/print export header -> the display column key that controls it. An export
// column whose controlling column is hidden is dropped, so the export matches
// the columns currently shown in the processed table. Columns with no export
// counterpart simply don't gate any export column.
const BROILER_EXPORT_HEADER_COLUMN: Record<string, string> = {
  Batch: 'name',
  'Record ID': 'name',
  Status: 'status',
  Breed: 'breed',
  // Hatchery rides in the Breed cell, so it is gated by that column's key.
  Hatchery: 'breed',
  Schooner: 'schooner',
  'Hatch date': 'hatchDate',
  'Processing date': 'processingDate',
  'Time on farm': 'timeOnFarm',
  'Birds ordered': 'birdCount',
  'Birds arrived': 'birdsArrived',
  'To processor': 'toProcessor',
  Mortality: 'mortality',
  'Starter feed lbs': 'starterFeed',
  'Grower feed lbs': 'growerFeed',
  'Total feed lbs': 'totalFeed',
  'Feed per processed bird': 'feedPerBird',
  'Avg dressed lbs': 'avgDressed',
};

// The ppp-v4 broiler batch row as this hub reads it, on top of the open record
// src/lib/broiler.ts declares for exactly these rows. Two groups of columns are
// narrowed past the shared declaration, and each for a reason that is about the
// code rather than about the database:
//
//   * `id` / `name` are required strings because this file dereferences them —
//     `b.name.toLowerCase()`, `del(b.id)`, `key={b.id}` — so a row without them
//     already threw or mis-keyed before this conversion. `String(...)` would
//     turn those crashes into the literal text "null", a behaviour change.
//   * the feed / cost / count columns are `number` because this file does BARE
//     arithmetic on them: `(b.brooderFeedLbs || 0) + (b.schoonerFeedLbs || 0)`,
//     `starterLbs * (b.perLbStarterCost || 0)`. That is addition only when both
//     sides are numbers and would silently CONCATENATE on a stored string, so
//     the declaration records a requirement the shipped code already has.
//     Coercing instead (`Number(...)`) would change what runs.
//
// Columns this file only reads through parseFloat / parseInt / Number keep the
// shared `number | string | null`, because there the stored-string tolerance is
// real and exercised.
type BroilerBatchRow = BroilerBatch & {
  id: string;
  name: string;
  hatchery?: string | null;
  brooder?: string | null;
  schooner?: string | null;
  brooderIn?: string | null;
  brooderOut?: string | null;
  schoonerIn?: string | null;
  schoonerOut?: string | null;
  birdCount?: number | null;
  mortalityCumulative?: number | null;
  brooderFeedLbs?: number | null;
  schoonerFeedLbs?: number | null;
  gritLbs?: number | null;
  perLbStarterCost?: number | null;
  perLbStandardCost?: number | null;
  perLbGritCost?: number | null;
  totalToProcessor?: number | string | null;
  birdCountActual?: number | string | null;
  chickCost?: number | string | null;
  processingCost?: number | string | null;
  avgDressedLbs?: number | string | null;
  avgBreastLbs?: number | string | null;
  avgThighsLbs?: number | string | null;
  week4Lbs?: number | string | null;
  week6Lbs?: number | string | null;
};

// A poultry_dailys row, as the mortality/feed reductions below read it.
type BroilerDailyRecord = BroilerDailyRow & {date?: string | null};

// The five $/lb rates FeedCostsContext held. Only three are read here, and only
// when an operator reopens a processed batch that never had a cost snapshot.
type BroilerFeedCostRates = {starter: number; grower: number; layer: number; pig: number; grit: number};

// Stable module-level empties, not inline literals. `broilerDailys` is a
// dependency of the `totalFeedLbsFor` callback and of four useMemos below, so a
// fresh `[]` on every pre-resolution render would rebuild all five and defeat
// the memoisation the sorted/filtered lists depend on.
const NO_BROILER_DAILYS: BroilerDailyRecord[] = [];
const NO_BROILER_BATCHES: BroilerBatchRow[] = [];
// A frozen params object for the same reason: useRouteResource serializes
// `params` into its memo key, and a new object literal per render would churn
// it.
const BROILER_BATCHES_HUB_PARAMS = Object.freeze({});

// The filter dict is opaque to this view — broilerBatchFilters.js owns its
// meaning — but the panel sets and clears keys BY NAME, so it is described the
// way it is used: the declared filter shape plus an open record. Same shape
// CattleHerdsView.tsx uses for the identical control set.
type FilterState = BroilerBatchFilters & Record<string, unknown>;
// One {key, dir} rule, widened past the frozen default's literal types because
// the picker below writes any of the BROILER_SORT_OPTIONS pairs into it.
type SortRule = {key: string; dir: string};
type DateRangeKey = 'startDateRange' | 'processingDateRange';
type NumericRangeKey =
  | 'birdCountRange'
  | 'birdsArrivedRange'
  | 'toProcessorRange'
  | 'mortalityRange'
  | 'lbsProducedRange';

// app_saved_views rows, narrowed to the columns the picker reads. savedViewsApi
// returns them as `unknown` because it does not read the table either.
type SavedViewRow = {
  id: string;
  name: string;
  visibility?: string | null;
  owner_profile_id?: string | null;
  view_state?: unknown;
};
type SavedViewStateBlob = {filters?: unknown; sortRules?: unknown; columns?: unknown};

// One column of the batches DataTable. Structurally the shared
// DataTable column, which the component does not export.
type ProcessedColumn = {
  key: string;
  label: string;
  primary?: boolean;
  align?: 'left' | 'right';
  mobilePriority?: boolean;
  render?: (batch: BroilerBatchRow) => React.ReactNode;
};

// Props exactly as BroilerEntry.tsx declares and passes them. The hub reads the
// first five; the other five exist because BroilerListRouter forwards the whole
// prop object to BroilerBatchPage, which is where the batch form is edited.
// `persistDocuments` is one of those pass-throughs — the hub never calls it, and
// its two status flips still build their own array and go through `persist`.
export type BroilerListProps = {
  openAdd: () => void;
  openEdit: (batch: unknown) => void;
  persist: (next: unknown[]) => Promise<boolean>;
  persistDocuments: (batchId: string | null, documents: unknown[]) => Promise<boolean>;
  del: (id: string, onDeleted?: () => void) => void;
  canDeleteAnything: (role: string | undefined) => boolean;
  upd: (key: string, value: unknown) => void;
  closeForm: () => Promise<boolean>;
  submit: (force: boolean) => Promise<boolean>;
  parseProcessorXlsx: (file: File) => Promise<void>;
};

// Same helper BroilerDailysView.tsx uses: `catch (e)` is `unknown` under strict,
// and every call site below built its message as `e.message || String(e)`.
function errorMessage(value: unknown): string {
  const candidate = value && typeof value === 'object' ? (value as {message?: unknown}).message : undefined;
  return String(candidate || value);
}

function broilerBatchHref(b: BroilerBatchRow) {
  return '/broiler/batches/' + encodeURIComponent(b && b.name ? b.name : '');
}

// R20 (§14): a list row has no actions, so the hub no longer writes. The
// props stay on BroilerListProps because the router hands the same object to
// BroilerBatchPage, which does use them.
function BroilerListHub(_props: BroilerListProps) {
  const navigate = useNavigate();
  const {confirmDelete} = useConfirm();
  // Same narrowing CattleHerdsView.tsx uses for the identical reads. AuthState's
  // third member is the signed-out `false`, which optional chaining does NOT
  // remove — `authState?.role` on `false` evaluates `false.role` and yields
  // undefined at runtime, exactly as it does for null. The cast lets the checker
  // see the property access the shipped code already makes.
  const {authState} = useAuth() as Omit<ReturnType<typeof useAuth>, 'authState'> & {
    authState: AuthIdentity | null;
  };
  const {setBatches} = useBatches();
  // The repaired batch directory, daily history, and feed rates all arrive
  // through this route resource. App's retained actions receive a layout-time
  // context mirror below, but this renderer never reads it back.
  const hubResource = useRouteResource(RESOURCE.BROILER_BATCHES_HUB, BROILER_BATCHES_HUB_PARAMS);
  const hubData = hubResource.data as BroilerBatchesHubData | null;
  const batches = (hubData?.batches as BroilerBatchRow[] | undefined) ?? NO_BROILER_BATCHES;
  // Keep the retained App form/write closures synchronized before paint. This
  // surface still only consumes the route resource: BatchesContext is a write
  // mirror, not its rendered source of truth.
  React.useLayoutEffect(() => {
    if (hubData) setBatches(batches);
  }, [batches, hubData, setBatches]);
  const broilerDailys = (hubData ? (hubData.broilerDailys as BroilerDailyRecord[]) : null) ?? NO_BROILER_DAILYS;

  const [listNotice, setListNotice] = React.useState<Notice | null>(null);
  const myProfileId = authState?.user?.id || null;

  // Filter + single-rule sort state. Persisted per surface so a refresh keeps
  // the operator's last filter/sort. filters is an opaque dict consumed by
  // buildBroilerBatchPredicate; sortRules holds a single {key, dir} rule
  // (right-sized) but is stored as an array to match the saved-view contract.
  // Seeded with the In Process chip so the page opens on the slice the owner
  // asked for; a returning operator's persisted filters still win over it.
  const [filters, setFilters] = usePersistentViewState('broiler.batches.filters', {
    ...BROILER_DEFAULT_FILTERS,
  } as FilterState);
  // Seeded from the default chip (In Process), so a first visit opens on
  // soonest-first rather than on the finished-history order.
  const [sortRules, setSortRules] = usePersistentViewState('broiler.batches.sortRules', [
    broilerSortForStatusChip(BROILER_DEFAULT_STATUS_CHIP) as SortRule,
  ]);
  const sortRule = sortRules[0] || {...BROILER_BATCH_DEFAULT_SORT};

  // Compact icon-panel toolbar (Cattle Herds parity): one tool panel open at a
  // time — 'savedViews' | 'filters' | 'sort' | null.
  const [openToolPanel, setOpenToolPanel] = React.useState<string | null>(null);
  function toggleToolPanel(panel: string) {
    setOpenToolPanel((cur) => (cur === panel ? null : panel));
  }

  // Which processed-table columns are shown (the column/display picker). Persisted
  // per surface and stored in saved views. `name` + row actions are always shown.
  const [visibleColumns, setVisibleColumns] = usePersistentViewState(
    'broiler.batches.columns',
    BROILER_DEFAULT_COLUMN_KEYS,
  );
  function toggleColumn(key: string) {
    setVisibleColumns((cur) => (cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]));
  }
  const columnVisible = (key: string) => key === 'name' || key === 'actions' || visibleColumns.includes(key);

  // Saved views (broiler.batches surface). Failure degrades gracefully — the
  // list + filters keep working; only the saved-views row shows a notice.
  const [selectedViewId, setSelectedViewId] = React.useState('');
  const [showSaveViewForm, setShowSaveViewForm] = React.useState(false);
  const [saveViewName, setSaveViewName] = React.useState('');
  const [saveViewVisibility, setSaveViewVisibility] = React.useState('private');
  const [savedViewBusy, setSavedViewBusy] = React.useState(false);
  const savedViewsQuery = useSavedViewsData(BROILER_BATCHES_SURFACE_KEY, {client: sb});
  const savedViews = savedViewsQuery.data as SavedViewRow[];
  const savedViewsError = savedViewsQuery.error ? savedViewsErrorMessage(savedViewsQuery.error) : null;
  const savedViewsLoading = savedViewsQuery.loading;
  const loadSavedViews = savedViewsQuery.refetch;

  React.useEffect(() => {
    if (savedViewsLoading || savedViewsError) return;
    setSelectedViewId((cur) => (cur && savedViews.some((row) => row.id === cur) ? cur : ''));
  }, [savedViews, savedViewsError, savedViewsLoading]);

  // Carry the visible order of the section the batch was clicked from into route
  // state so the record page can show prev/next. Sequence ids are batch ids;
  // labels are batch names (BroilerBatchPage resolves id -> current name).
  function openBatch(b: BroilerBatchRow, seqRows?: BroilerBatchRow[]) {
    if (!b || !b.name) return;
    const opts = seqRows ? recordSeqNavOptions(labeledSeqItems(seqRows, 'name')) : undefined;
    navigate(broilerBatchHref(b), opts);
  }

  // Total feed lbs produced for a batch (daily-reports-aware, with the legacy
  // manual fallback). Single source for the export row, the "lbsProduced" sort
  // key (via ctx.totalFeedLbsOf), and the row Feed cell semantics.
  const totalFeedLbsFor = React.useCallback(
    (batch: BroilerBatchRow) => {
      const n = (value: unknown) => parseFloat(String(value)) || 0;
      const stats = calcBroilerStatsFromDailys(batch, broilerDailys);
      const useManualFeedFallback = !stats.legacy && stats.starterFeed === 0 && stats.growerFeed === 0;
      const starterLbs = useManualFeedFallback ? n(batch.brooderFeedLbs) : stats.starterFeed;
      const growerLbs = useManualFeedFallback ? n(batch.schoonerFeedLbs) : stats.growerFeed;
      return starterLbs + growerLbs;
    },
    [broilerDailys],
  );

  // ── filter + sort scope ───────────────────────────────────────────────────
  // R20 / the owner's note: ONE table over every batch. The status chips pick
  // the lifecycle slice by writing the `status` filter, and the
  // search/filter/sort + column controls then apply to that slice — nothing is
  // pinned on top, and no section is exempt from the controls. Status is the
  // EFFECTIVE poultry status so the chip, the filter and the badge each row
  // renders all agree.
  // broilerBatchFilters.js declares the row shape it hands its two resolvers;
  // src/lib/broiler.js declares the one the status/stats helpers take. Both
  // describe the same app_store row, and this is the single seam between them.
  const filterCtx = React.useMemo<BroilerBatchContext>(
    () => ({
      statusOf: (b) => calcPoultryStatus(b as BroilerBatchRow),
      totalFeedLbsOf: (b) => totalFeedLbsFor(b as BroilerBatchRow),
    }),
    [totalFeedLbsFor],
  );
  const filtered = React.useMemo(
    () => batches.filter(buildBroilerBatchPredicate(filters, filterCtx)),
    [batches, filters, filterCtx],
  );
  const cardRows = React.useMemo(
    () => [...filtered].sort(buildBroilerBatchComparator(sortRule, filterCtx)),
    [filtered, sortRule, filterCtx],
  );

  // Chip counts are over EVERY batch, never the search-filtered set, so the
  // chips keep reporting the size of each slice while a search narrows the table.
  // calcPoultryStatus directly rather than filterCtx.statusOf: the context's
  // resolver is optional in its declaration, and this is the same function the
  // context is wired to two lines above, so the chips cannot drift from the
  // filter they drive.
  const statusChipCounts = React.useMemo(() => {
    const counts: Record<string, number> = {};
    for (const b of batches) {
      const st = calcPoultryStatus(b);
      counts[st] = (counts[st] || 0) + 1;
    }
    return counts;
  }, [batches]);
  const statusChips = React.useMemo(
    () =>
      BROILER_STATUS_CHIPS.map((c) => ({
        key: c.key,
        label: c.label,
        count: c.statuses ? c.statuses.reduce((s, st) => s + (statusChipCounts[st] || 0), 0) : batches.length,
      })),
    [statusChipCounts, batches.length],
  );
  // Read all selected chips from the existing array filter, so saved views,
  // exports and the table use exactly the same status combination.
  const selectedStatusChips = React.useMemo(() => {
    const sel = filters.status;
    if (!Array.isArray(sel) || sel.length === 0) return BROILER_STATUS_CHIPS.map((chip) => chip.key);
    const statuses = sel.map((status) => String(status ?? '').toLowerCase());
    const selected = BROILER_STATUS_CHIPS.filter((chip) =>
      chip.statuses?.some((status) => statuses.includes(status)),
    ).map((chip) => chip.key);
    return selected.length === BROILER_STATUS_CHIPS.length - 1 ? ['all', ...selected] : selected;
  }, [filters.status]);
  const selectedChipLabel = selectedStatusChips.includes('all')
    ? 'all'
    : BROILER_STATUS_CHIPS.filter((chip) => selectedStatusChips.includes(chip.key))
        .map((chip) => chip.label.toLowerCase())
        .join(' + ') || 'selected statuses';

  const observedBreeds = React.useMemo(() => [...new Set(batches.map((b) => b.breed).filter(Boolean))], [batches]);
  const breedFilterOptions = React.useMemo(
    () => broilerBreedFilterOptions(observedBreeds, breedLabel),
    [observedBreeds],
  );

  const totalCount = batches.length;
  // The size of the chip's slice, which is what "showing X of Y" counts against.
  const chipTotal = selectedStatusChips.includes('all')
    ? totalCount
    : statusChips.reduce((total, chip) => total + (selectedStatusChips.includes(chip.key) ? chip.count : 0), 0);
  const visibleCount = cardRows.length;
  // The status chip is itself a filter, so it is not counted as one of the
  // extra filters the toolbar reports.
  const filterCount = Object.keys(filters).filter((k) => k !== 'status').length;

  function setFilter(key: string, value: unknown) {
    setFilters((prev) => {
      const next = {...prev};
      if (
        value == null ||
        value === '' ||
        (Array.isArray(value) && value.length === 0) ||
        (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0)
      ) {
        delete next[key];
      } else {
        next[key] = value;
      }
      return next;
    });
  }
  // Clear returns the page to its DEFAULT view, which includes the In Process
  // chip — not to an empty filter set, which would silently widen the table to
  // every batch the moment someone cleared a breed filter.
  function clearAllFilters() {
    setFilters({...BROILER_DEFAULT_FILTERS});
  }
  // The chips write the one `status` filter, so the slice travels with saved
  // views, CSV/print and the record sequence without any state of its own.
  function selectStatusChip(key: string) {
    const chip = BROILER_STATUS_CHIPS.find((c) => c.key === key);
    if (!chip) return;
    if (!chip.statuses) {
      setFilter('status', null);
      setSortRules([broilerSortForStatusChip('all') as SortRule]);
      return;
    }
    const selected = BROILER_STATUS_CHIPS.filter((entry) => entry.statuses && selectedStatusChips.includes(entry.key));
    const next = selectedStatusChips.includes(key)
      ? selected.filter((entry) => entry.key !== key)
      : [...selected, chip];
    // Keep one status selected: the existing predicate treats an empty array
    // as All, which would unexpectedly widen the list after a deselection.
    if (next.length === 0) return;
    setFilter(
      'status',
      next.flatMap((entry) => (entry.statuses ? [...entry.statuses] : [])),
    );
    // Combinations with work ahead read soonest-first; completed history and
    // All retain their existing newest-first order.
    const sortChip =
      next.length === BROILER_STATUS_CHIPS.length - 1
        ? 'all'
        : next.some((entry) => entry.key !== 'complete')
          ? 'in_process'
          : 'complete';
    setSortRules([broilerSortForStatusChip(sortChip) as SortRule]);
  }
  // Single-select multi-value filter (breed/hatchery/brooder/schooner):
  // stored as a one-element array so the predicate's array matcher applies.
  function setSelectFilter(key: string, value: string) {
    setFilter(key, value ? [value] : null);
  }
  function selectFilterValue(key: string) {
    const selected = filters[key];
    return Array.isArray(selected) && selected.length === 1 ? selected[0] : '';
  }
  // Date-range bound setter for any *DateRange filter (startDateRange / processingDateRange).
  function setDateBound(rangeKey: DateRangeKey, bound: 'after' | 'before', value: string) {
    setFilters((prev) => {
      const range = {...(prev[rangeKey] || {})};
      if (value) range[bound] = value;
      else delete range[bound];
      const next = {...prev};
      if (!range.after && !range.before) delete next[rangeKey];
      else next[rangeKey] = range;
      return next;
    });
  }
  // Numeric-range bound setter for any *Range filter (birdCountRange, etc.).
  function setNumBound(rangeKey: NumericRangeKey, bound: 'min' | 'max', value: string) {
    setFilters((prev) => {
      const range = {...(prev[rangeKey] || {})};
      if (value !== '' && value != null) range[bound] = value;
      else delete range[bound];
      const next = {...prev};
      if ((range.min == null || range.min === '') && (range.max == null || range.max === '')) delete next[rangeKey];
      else next[rangeKey] = range;
      return next;
    });
  }
  function setSortValue(value: string) {
    const opt = BROILER_SORT_OPTIONS.find((o) => o.value === value) || BROILER_SORT_OPTIONS[0];
    setSortRules([{key: opt.key, dir: opt.dir}]);
  }
  function flipSortDir() {
    setSortRules([{key: sortRule.key, dir: sortRule.dir === 'asc' ? 'desc' : 'asc'}]);
  }

  const search = filters.textSearch || '';
  const dateBound = (rangeKey: DateRangeKey, bound: 'after' | 'before') =>
    (filters[rangeKey] && filters[rangeKey][bound]) || '';
  const numBound = (rangeKey: NumericRangeKey, bound: 'min' | 'max') => {
    const v = filters[rangeKey] && filters[rangeKey][bound];
    return v == null ? '' : v;
  };
  const sortValue =
    BROILER_SORT_OPTIONS.find((o) => o.key === sortRule.key && o.dir === sortRule.dir)?.value || 'processingDate:desc';

  const hatcheryOptions = React.useMemo(() => broilerDistinctFieldValues(batches, 'hatchery'), [batches]);
  const brooderOptions = React.useMemo(() => broilerDistinctFieldValues(batches, 'brooder'), [batches]);
  const schoonerOptions = React.useMemo(() => broilerDistinctFieldValues(batches, 'schooner'), [batches]);

  // ── saved views ────────────────────────────────────────────────────────────
  const selectedView = savedViews.find((v) => v.id === selectedViewId) || null;
  const selectedViewIsMine = !!(selectedView && myProfileId && selectedView.owner_profile_id === myProfileId);
  const myViews = savedViews.filter((v) => myProfileId && v.owner_profile_id === myProfileId);
  const publicOtherViews = savedViews.filter(
    (v) => v.visibility === 'public' && !(myProfileId && v.owner_profile_id === myProfileId),
  );
  function broilerBatchesViewState() {
    return {...buildViewState({filters, sortRules, viewMode: 'grouped'}), columns: visibleColumns};
  }
  function applyBroilerSavedView(view: SavedViewRow | undefined) {
    if (!view) return;
    const st = (view.view_state || {}) as SavedViewStateBlob;
    setFilters(st.filters && typeof st.filters === 'object' ? (st.filters as FilterState) : ({} as FilterState));
    setSortRules(
      Array.isArray(st.sortRules) && st.sortRules.length > 0 ? [st.sortRules[0]] : [{...BROILER_BATCH_DEFAULT_SORT}],
    );
    setVisibleColumns(Array.isArray(st.columns) && st.columns.length > 0 ? st.columns : BROILER_DEFAULT_COLUMN_KEYS);
    setOpenToolPanel(null);
  }
  function onSelectSavedView(id: string) {
    setSelectedViewId(id);
    if (!id) return;
    applyBroilerSavedView(savedViews.find((v) => v.id === id));
  }
  function openSaveViewForm() {
    setSaveViewName('');
    setSaveViewVisibility('private');
    setShowSaveViewForm(true);
  }
  async function submitSaveView() {
    const name = saveViewName.trim();
    if (!name) {
      setListNotice({kind: 'error', message: 'Name the view before saving.'});
      return;
    }
    setSavedViewBusy(true);
    try {
      const created = (await createSavedView(sb, {
        surfaceKey: BROILER_BATCHES_SURFACE_KEY,
        name,
        visibility: saveViewVisibility,
        viewState: broilerBatchesViewState(),
      })) as {id?: string} | null;
      setShowSaveViewForm(false);
      await loadSavedViews();
      if (created?.id) setSelectedViewId(created.id);
    } catch (e) {
      setListNotice({kind: 'error', message: 'Save view failed: ' + errorMessage(e)});
    } finally {
      setSavedViewBusy(false);
    }
  }
  async function updateSelectedView() {
    if (!selectedView || !selectedViewIsMine) return;
    setSavedViewBusy(true);
    try {
      await updateSavedView(sb, selectedView.id, {viewState: broilerBatchesViewState()});
      await loadSavedViews();
      setListNotice({
        kind: 'success',
        message: 'Updated "' + selectedView.name + '" to the current search/filter/sort.',
      });
    } catch (e) {
      setListNotice({kind: 'error', message: 'Update view failed: ' + errorMessage(e)});
    } finally {
      setSavedViewBusy(false);
    }
  }
  async function proceedDeleteSelectedView(view: SavedViewRow) {
    setSavedViewBusy(true);
    try {
      await deleteSavedView(sb, view.id);
      setSelectedViewId('');
      await loadSavedViews();
      setListNotice({kind: 'success', message: 'Deleted saved view "' + view.name + '".'});
    } catch (e) {
      setListNotice({kind: 'error', message: 'Delete view failed: ' + errorMessage(e)});
    } finally {
      setSavedViewBusy(false);
    }
  }
  function deleteSelectedView() {
    if (!selectedView || !selectedViewIsMine) return;
    const view = selectedView;
    const run = () => {
      void proceedDeleteSelectedView(view);
    };
    // confirmDelete comes from ConfirmContext, so it is always a function here;
    // the old `if (confirmDelete) ... else delete anyway` fallback could drop
    // the confirmation entirely and is gone.
    confirmDelete('Delete saved view "' + view.name + '"?', run);
  }

  // ── export — the processed table's filtered + sorted rows, and only the
  // columns currently shown (the column picker drives the export too). ─────────
  const broilerExportRows = cardRows.map((batch) => {
    const n = (value: unknown) => parseFloat(String(value)) || 0;
    const stats = calcBroilerStatsFromDailys(batch, broilerDailys);
    const useManualFeedFallback = !stats.legacy && stats.starterFeed === 0 && stats.growerFeed === 0;
    const starterLbs = useManualFeedFallback ? n(batch.brooderFeedLbs) : stats.starterFeed;
    const growerLbs = useManualFeedFallback ? n(batch.schoonerFeedLbs) : stats.growerFeed;
    const totalFeed = starterLbs + growerLbs;
    const processed = n(batch.totalToProcessor);
    let timeOnFarm = '';
    if (batch.hatchDate && batch.processingDate) {
      const days = Math.round(
        (new Date(batch.processingDate + 'T12:00:00').getTime() - new Date(batch.hatchDate + 'T12:00:00').getTime()) /
          86400000,
      );
      timeOnFarm = Math.floor(days / 7) + 'w ' + (days % 7) + 'd';
    }
    return {
      ...batch,
      export_status: calcPoultryStatus(batch),
      time_on_farm: timeOnFarm,
      export_mortality: stats.mortality,
      export_starter_lbs: starterLbs,
      export_grower_lbs: growerLbs,
      export_total_feed_lbs: totalFeed,
      export_feed_per_processed_bird: processed > 0 && totalFeed > 0 ? totalFeed / processed : null,
    };
  });
  const exportColumns = buildBroilerBatchExportColumns({fmt}).filter((c) => {
    const key = BROILER_EXPORT_HEADER_COLUMN[c.header];
    return key ? columnVisible(key) : true;
  });

  function handleExportCsv() {
    if (!broilerExportRows.length) {
      setListNotice({kind: 'warning', message: 'No broiler batches to export.'});
      return;
    }
    const ok = downloadCsv(csvFilename('broiler-batches'), rowsToCsv(exportColumns, broilerExportRows));
    if (!ok) setListNotice({kind: 'warning', message: 'CSV export is unavailable in this browser.'});
  }

  function handlePrintRows() {
    if (!broilerExportRows.length) {
      setListNotice({kind: 'warning', message: 'No broiler batches to print.'});
      return;
    }
    const ok = printRows({
      title: 'Broiler Batches',
      subtitle: broilerExportRows.length + ' batches',
      columns: exportColumns,
      rows: broilerExportRows,
    });
    if (!ok) setListNotice({kind: 'warning', message: 'Print export is unavailable in this browser.'});
  }

  const inpS: CSSProperties = {
    fontSize: 13,
    padding: '7px 10px',
    border: '1px solid var(--border-strong)',
    borderRadius: 10,
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  };
  const savedViewGhostBtnS: CSSProperties = {
    padding: '6px 12px',
    borderRadius: 10,
    border: '1px solid var(--border-strong)',
    background: 'white',
    color: 'var(--ink)',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    whiteSpace: 'nowrap',
  };
  const savedViewPrimaryBtnS = {...savedViewGhostBtnS, border: '1px solid #187A54', color: '#187A54'};
  const savedViewRadioLabelS: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 12,
    color: 'var(--ink)',
    cursor: 'pointer',
  };
  // Compact icon button + tool panel (Cattle Herds parity).
  const BROILER_ACCENT = getProgramColor('broiler');
  // Selected tool button = solid program-amber fill with a white glyph (matches
  // the filled program tab in the top nav); unselected = white with dark glyph.
  const toolButtonS = (active = false): CSSProperties => ({
    width: 34,
    height: 34,
    borderRadius: 10,
    border: active ? `1px solid ${BROILER_ACCENT}` : '1px solid var(--border-strong)',
    background: active ? BROILER_ACCENT : 'white',
    color: active ? '#fff' : 'var(--ink)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'inherit',
    flex: '0 0 auto',
  });
  const toolPanelS: CSSProperties = {
    background: 'white',
    border: '1px solid var(--border)',
    borderRadius: 10,
    padding: '12px 16px',
    marginBottom: 8,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  };
  const filterLabelS: CSSProperties = {
    fontSize: 11,
    color: 'var(--ink-muted)',
    display: 'inline-flex',
    flexDirection: 'column',
    gap: 3,
    fontWeight: 600,
  };
  const numInpS = {...inpS, width: 84};

  if (hubResource.loading) {
    return (
      <div style={{minHeight: '100vh', background: 'var(--bg-page)'}}>
        <Header />
        <div style={{padding: '1rem'}} data-broiler-batches-loaded="false">
          Loading broiler batchesâ€¦
        </div>
      </div>
    );
  }

  if (hubResource.error) {
    return (
      <div style={{minHeight: '100vh', background: 'var(--bg-page)'}}>
        <Header />
        <div style={{padding: '1rem'}} data-broiler-batches-loaded="false">
          <InlineNotice
            notice={{
              kind: 'error',
              message: 'Could not load broiler batches. Please try again. (' + errorMessage(hubResource.error) + ')',
            }}
          />
          <Button type="button" onClick={() => void hubResource.refetch()} style={{marginTop: 10}}>
            Try again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div style={{minHeight: '100vh', background: 'var(--bg-page)'}}>
      <Header />
      <div
        style={{padding: '1rem', maxWidth: 1180, margin: '0 auto'}}
        data-broiler-batches-loaded={hubData ? 'true' : 'false'}
      >
        <h1 className="poultry-page-title" style={{marginBottom: 20}}>
          Broiler Batches
        </h1>
        <div style={{marginTop: 12}}>
          {/* Compact icon-panel toolbar — Cattle Herds parity (controls the one table below) */}
          <div
            data-broiler-batches-toolbar
            style={{display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8}}
          >
            <input
              type="text"
              value={search}
              onChange={(e) => setFilter('textSearch', e.target.value)}
              placeholder="Search batch name, breed, hatchery..."
              data-broiler-search
              aria-label="Search broiler batches"
              style={{...inpS, flex: 1, minWidth: 180}}
            />
            <Button
              type="button"
              data-broiler-batches-saved-views-toggle="1"
              aria-label="Saved views"
              title="Saved views"
              aria-expanded={openToolPanel === 'savedViews'}
              onClick={() => toggleToolPanel('savedViews')}
              style={{...(openToolPanel === 'savedViews' ? {borderColor: 'var(--brand)', color: 'var(--brand)'} : {})}}
            >
              Saved Views
            </Button>
            <Button
              type="button"
              data-broiler-batches-filters-toggle="1"
              aria-label="Filters"
              title="Filters"
              aria-expanded={openToolPanel === 'filters'}
              onClick={() => toggleToolPanel('filters')}
              style={{...(openToolPanel === 'filters' ? {borderColor: 'var(--brand)', color: 'var(--brand)'} : {})}}
            >
              {filterCount > 0 ? 'Filters (' + filterCount + ')' : 'Filters'}
            </Button>
            <Button
              type="button"
              data-broiler-batches-sort-toggle="1"
              aria-label="Sort"
              title="Sort"
              aria-expanded={openToolPanel === 'sort'}
              onClick={() => toggleToolPanel('sort')}
              style={{...(openToolPanel === 'sort' ? {borderColor: 'var(--brand)', color: 'var(--brand)'} : {})}}
            >
              Sort
            </Button>
            <span style={{position: 'relative', flex: '0 0 auto'}}>
              <Button
                type="button"
                data-broiler-batches-columns-toggle="1"
                aria-label="Columns"
                title="Columns shown in the table"
                aria-expanded={openToolPanel === 'columns'}
                onClick={() => toggleToolPanel('columns')}
                style={{...(openToolPanel === 'columns' ? {borderColor: 'var(--brand)', color: 'var(--brand)'} : {})}}
              >
                Columns
              </Button>
              {openToolPanel === 'columns' && (
                <div
                  data-broiler-batches-columns
                  data-mobile-popover="1"
                  style={{
                    position: 'absolute',
                    top: 40,
                    right: 0,
                    zIndex: 40,
                    width: 440,
                    maxWidth: '92vw',
                    maxHeight: 520,
                    overflowY: 'auto',
                    background: 'white',
                    border: '1px solid var(--border-strong)',
                    borderRadius: 12,
                    boxShadow: '0 8px 24px rgba(20,30,40,.16)',
                    padding: '8px 8px 10px',
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      color: 'var(--ink-muted)',
                      fontWeight: 700,
                      padding: '4px 6px 8px',
                      borderBottom: '1px solid var(--border)',
                      marginBottom: 6,
                    }}
                  >
                    Columns shown ({visibleColumns.length}/{BROILER_BATCH_COLUMNS.length})
                  </div>
                  <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 10px'}}>
                    {BROILER_BATCH_COLUMNS.map((c) => (
                      <label
                        key={c.key}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 9,
                          padding: '6px 8px',
                          borderRadius: 10,
                          cursor: 'pointer',
                          fontSize: 13,
                          color: 'var(--ink)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={visibleColumns.includes(c.key)}
                          onChange={() => toggleColumn(c.key)}
                          data-broiler-column-toggle={c.key}
                          style={{flex: '0 0 auto', margin: 0, width: 15, height: 15}}
                        />
                        <span>{c.label}</span>
                      </label>
                    ))}
                  </div>
                  <div
                    style={{
                      borderTop: '1px solid var(--border)',
                      marginTop: 6,
                      paddingTop: 8,
                      display: 'flex',
                      gap: 8,
                      justifyContent: 'flex-end',
                    }}
                  >
                    <Button
                      type="button"
                      onClick={() => setVisibleColumns(BROILER_BATCH_COLUMNS.map((c) => c.key))}
                      data-broiler-columns-all
                    >
                      Show all
                    </Button>
                    <Button
                      type="button"
                      onClick={() => setVisibleColumns(BROILER_DEFAULT_COLUMN_KEYS)}
                      data-broiler-columns-reset
                    >
                      Reset
                    </Button>
                  </div>
                </div>
              )}
            </span>
            <Button
              type="button"
              data-broiler-batches-export-csv="1"
              aria-label="Export CSV"
              title="Export CSV"
              onClick={handleExportCsv}
            >
              CSV
            </Button>
            <Button
              type="button"
              data-broiler-batches-print="1"
              aria-label="Print"
              title="Print"
              onClick={handlePrintRows}
            >
              ⎙
            </Button>
          </div>

          <InlineNotice notice={listNotice} onDismiss={() => setListNotice(null)} />

          {/* Saved views panel */}
          {openToolPanel === 'savedViews' && (
            <div data-broiler-saved-views-row style={toolPanelS}>
              <div style={{display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap'}}>
                <span style={{fontSize: 11, color: 'var(--ink-muted)', fontWeight: 600}}>Saved views</span>
                {savedViewsError ? (
                  <span style={{fontSize: 12, color: '#b91c1c'}} data-broiler-saved-views-error>
                    Saved views unavailable. Filters still work.
                  </span>
                ) : (
                  <>
                    <select
                      data-broiler-saved-view-select
                      value={selectedViewId}
                      disabled={savedViewsLoading}
                      onChange={(e) => onSelectSavedView(e.target.value)}
                      style={{...inpS, width: 'auto', minWidth: 200, fontSize: 12, padding: '6px 10px'}}
                    >
                      <option value="">{savedViewsLoading ? 'Loading...' : 'Select a saved view'}</option>
                      {myViews.length > 0 && (
                        <optgroup label="My views">
                          {myViews.map((v) => (
                            <option key={v.id} value={v.id}>
                              {v.name + (v.visibility === 'public' ? ' - public' : ' - private')}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {publicOtherViews.length > 0 && (
                        <optgroup label="Public views">
                          {publicOtherViews.map((v) => (
                            <option key={v.id} value={v.id}>
                              {v.name}
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </select>
                    {selectedViewIsMine && (
                      <>
                        <Button
                          type="button"
                          data-broiler-saved-view-update
                          onClick={updateSelectedView}
                          disabled={savedViewBusy}
                        >
                          Update to current
                        </Button>
                        <Button
                          variant="danger"
                          type="button"
                          data-broiler-saved-view-delete
                          onClick={deleteSelectedView}
                          disabled={savedViewBusy}
                        >
                          Delete
                        </Button>
                      </>
                    )}
                    <span style={{flex: 1}} />
                    <Button
                      type="button"
                      data-broiler-saved-view-save-open
                      onClick={openSaveViewForm}
                      disabled={savedViewBusy}
                    >
                      Save current view
                    </Button>
                  </>
                )}
              </div>
              {showSaveViewForm && (
                <div
                  data-broiler-saved-view-form
                  style={{display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap'}}
                >
                  <input
                    data-broiler-saved-view-name
                    type="text"
                    value={saveViewName}
                    placeholder="View name"
                    onChange={(e) => setSaveViewName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') submitSaveView();
                    }}
                    style={{...inpS, flex: 1, minWidth: 200}}
                  />
                  <label style={savedViewRadioLabelS}>
                    <input
                      type="radio"
                      name="saveBroilerViewVisibility"
                      checked={saveViewVisibility === 'private'}
                      onChange={() => setSaveViewVisibility('private')}
                      data-broiler-saved-view-visibility="private"
                    />
                    Private
                  </label>
                  <label style={savedViewRadioLabelS}>
                    <input
                      type="radio"
                      name="saveBroilerViewVisibility"
                      checked={saveViewVisibility === 'public'}
                      onChange={() => setSaveViewVisibility('public')}
                      data-broiler-saved-view-visibility="public"
                    />
                    Public
                  </label>
                  <Button type="button" data-broiler-saved-view-save onClick={submitSaveView} disabled={savedViewBusy}>
                    Save
                  </Button>
                  <Button type="button" onClick={() => setShowSaveViewForm(false)} disabled={savedViewBusy}>
                    Cancel
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Filters panel */}
          {openToolPanel === 'filters' && (
            <div data-broiler-batches-filters style={toolPanelS}>
              <div style={{fontSize: 11, color: 'var(--ink-muted)', fontWeight: 600}}>
                Filters apply to the complete batches below
              </div>
              <div style={{display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end'}}>
                <label style={filterLabelS}>
                  Breed
                  <select
                    value={selectFilterValue('breed')}
                    onChange={(e) => setSelectFilter('breed', e.target.value)}
                    data-broiler-breed-filter
                    style={{...inpS, width: 'auto'}}
                  >
                    <option value="">All</option>
                    {breedFilterOptions.map((o) => (
                      <option key={o.code} value={o.code}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label style={filterLabelS}>
                  Hatchery
                  <select
                    value={selectFilterValue('hatchery')}
                    onChange={(e) => setSelectFilter('hatchery', e.target.value)}
                    data-broiler-hatchery-filter
                    style={{...inpS, width: 'auto'}}
                  >
                    <option value="">All</option>
                    {hatcheryOptions.map((v) => (
                      <option key={v} value={v}>
                        {v}
                      </option>
                    ))}
                  </select>
                </label>
                <label style={filterLabelS}>
                  Brooder
                  <select
                    value={selectFilterValue('brooder')}
                    onChange={(e) => setSelectFilter('brooder', e.target.value)}
                    data-broiler-brooder-filter
                    style={{...inpS, width: 'auto'}}
                  >
                    <option value="">All</option>
                    {brooderOptions.map((v) => (
                      <option key={v} value={v}>
                        {v}
                      </option>
                    ))}
                  </select>
                </label>
                <label style={filterLabelS}>
                  Schooner
                  <select
                    value={selectFilterValue('schooner')}
                    onChange={(e) => setSelectFilter('schooner', e.target.value)}
                    data-broiler-schooner-filter
                    style={{...inpS, width: 'auto'}}
                  >
                    <option value="">All</option>
                    {schoonerOptions.map((v) => (
                      <option key={v} value={v}>
                        {v}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div style={{display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end'}}>
                <label style={filterLabelS}>
                  Hatch date
                  <span style={{display: 'inline-flex', gap: 4, alignItems: 'center'}}>
                    <input
                      type="date"
                      value={dateBound('startDateRange', 'after')}
                      onChange={(e) => setDateBound('startDateRange', 'after', e.target.value)}
                      data-broiler-start-after
                      style={{...inpS, width: 'auto'}}
                    />
                    <span>–</span>
                    <input
                      type="date"
                      value={dateBound('startDateRange', 'before')}
                      onChange={(e) => setDateBound('startDateRange', 'before', e.target.value)}
                      data-broiler-start-before
                      style={{...inpS, width: 'auto'}}
                    />
                  </span>
                </label>
                <label style={filterLabelS}>
                  Processing date
                  <span style={{display: 'inline-flex', gap: 4, alignItems: 'center'}}>
                    <input
                      type="date"
                      value={dateBound('processingDateRange', 'after')}
                      onChange={(e) => setDateBound('processingDateRange', 'after', e.target.value)}
                      data-broiler-processing-after
                      style={{...inpS, width: 'auto'}}
                    />
                    <span>–</span>
                    <input
                      type="date"
                      value={dateBound('processingDateRange', 'before')}
                      onChange={(e) => setDateBound('processingDateRange', 'before', e.target.value)}
                      data-broiler-processing-before
                      style={{...inpS, width: 'auto'}}
                    />
                  </span>
                </label>
              </div>
              <div style={{display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end'}}>
                {(
                  [
                    {key: 'birdCountRange', label: 'Birds', hook: 'birdcount'},
                    {key: 'birdsArrivedRange', label: 'Birds arrived', hook: 'birdsarrived'},
                    {key: 'toProcessorRange', label: 'To processor', hook: 'toprocessor'},
                    {key: 'mortalityRange', label: 'Mortality', hook: 'mortality'},
                    {key: 'lbsProducedRange', label: 'Feed lbs', hook: 'lbsproduced'},
                  ] as {key: NumericRangeKey; label: string; hook: string}[]
                ).map((r) => (
                  <label key={r.key} style={filterLabelS}>
                    {r.label}
                    <span style={{display: 'inline-flex', gap: 4, alignItems: 'center'}}>
                      <input
                        type="number"
                        value={numBound(r.key, 'min')}
                        onChange={(e) => setNumBound(r.key, 'min', e.target.value)}
                        placeholder="min"
                        data-broiler-range-min={r.hook}
                        style={numInpS}
                      />
                      <span>–</span>
                      <input
                        type="number"
                        value={numBound(r.key, 'max')}
                        onChange={(e) => setNumBound(r.key, 'max', e.target.value)}
                        placeholder="max"
                        data-broiler-range-max={r.hook}
                        style={numInpS}
                      />
                    </span>
                  </label>
                ))}
              </div>
              {filterCount > 0 && (
                <div>
                  <Button type="button" onClick={clearAllFilters} data-broiler-clear-filters>
                    Clear all filters
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Sort panel */}
          {openToolPanel === 'sort' && (
            <div data-broiler-batches-sort-panel style={toolPanelS}>
              <div style={{display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap'}}>
                <span style={{fontSize: 11, color: 'var(--ink-muted)', fontWeight: 600}}>Sort by</span>
                <select
                  value={sortValue}
                  onChange={(e) => setSortValue(e.target.value)}
                  data-broiler-sort
                  style={{...inpS, width: 'auto'}}
                >
                  {BROILER_SORT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <Button type="button" onClick={flipSortDir} data-broiler-sort-dir title="Flip sort direction">
                  {sortRule.dir === 'desc' ? '↓ Desc' : '↑ Asc'}
                </Button>
              </div>
            </div>
          )}

          {/* The status chips are the only lifecycle control: one table, and
              chips pick any combination (R20, and the owner's note superseding the
              three-section design). The summary carries data-broiler-count. */}
          <div style={{marginBottom: 12}}>
            <StatusChipRow
              chips={statusChips}
              selected={selectedStatusChips}
              onSelect={selectStatusChip}
              rootAttrs={{
                'data-broiler-batches-status-chips': '1',
                role: 'group',
                'aria-label': 'Filter by batch status',
              }}
              summary={
                <span data-broiler-count>
                  Showing {visibleCount} of {chipTotal} {selectedChipLabel}
                  {filterCount > 0 && ' · ' + filterCount + ' filter' + (filterCount === 1 ? '' : 's')}
                </span>
              }
            />
          </div>

          {/* ── The batches table ──
            One canonical shared DataTable over every batch the chip and the
            filters leave. Numbers are black (DataTable's is-num cells), status
            is a single <Badge> reading the row's own effective status, borders
            are the DataTable hairlines, and the row chevron comes from
            onRowOpen. That handler is the same openBatch() drill-in as before,
            so the record-page route + prev/next sequence is unchanged. Per-batch
            feed/grit/cost breakdown, weigh-in weights, and birds-arrived remain
            on the batch RECORD page (the drill-in) — no data is lost.
            Never gated on a count: with no batches at all this block is what
            renders the empty state, so hiding it would leave the page blank. */}
          {(() => {
            // Per-batch derived stats (same source logic as the prior cards):
            // B-24-* uses manually entered feed totals; B-25+ pulls daily reports.
            const statsFor = (b: BroilerBatchRow) => {
              const isB24 = /^b-24-/i.test(b.name);
              const bd = broilerDailys.filter(
                (d) =>
                  (d.batch_label || '')
                    .toLowerCase()
                    .trim()
                    .replace(/^\(processed\)\s*/, '')
                    .trim() === b.name.toLowerCase().trim(),
              );
              const dailyStarterLbs = bd
                .filter((d) => d.feed_type === 'STARTER')
                .reduce((s, d) => s + (parseFloat(String(d.feed_lbs)) || 0), 0);
              const dailyGrowerLbs = bd
                .filter((d) => d.feed_type === 'GROWER')
                .reduce((s, d) => s + (parseFloat(String(d.feed_lbs)) || 0), 0);
              const dailyGritLbs = bd.reduce((s, d) => s + (parseFloat(String(d.grit_lbs)) || 0), 0);
              const dailyMortality = bd.reduce((s, d) => s + (parseInt(String(d.mortality_count)) || 0), 0);
              const starterLbs = isB24 ? b.brooderFeedLbs || 0 : dailyStarterLbs;
              const growerLbs = isB24 ? b.schoonerFeedLbs || 0 : dailyGrowerLbs;
              const gritLbs = isB24 ? b.gritLbs || 0 : dailyGritLbs;
              const mortality = isB24 ? b.mortalityCumulative || 0 : dailyMortality;
              const totalFeed = starterLbs + growerLbs;
              const feedCost = starterLbs * (b.perLbStarterCost || 0) + growerLbs * (b.perLbStandardCost || 0);
              const gritCost = gritLbs * (b.perLbGritCost || 0);
              const chickCost = parseFloat(String(b.chickCost)) || 0;
              const processingCost = parseFloat(String(b.processingCost)) || 0;
              const totalCost = feedCost + gritCost + processingCost + chickCost;
              const toProc = parseFloat(String(b.totalToProcessor)) || 0;
              const perBird = toProc > 0 ? totalCost / toProc : 0;
              const feedPerBird = toProc > 0 ? totalFeed / toProc : 0;
              const processingPerBird = toProc > 0 ? processingCost / toProc : 0;
              const totalMeat = (parseFloat(String(b.avgDressedLbs)) || 0) * toProc;
              const mortalityPct = Number(b.birdCount) > 0 ? ((mortality / Number(b.birdCount)) * 100).toFixed(1) : '0';
              const tofDays =
                b.hatchDate && b.processingDate
                  ? Math.round(
                      (new Date(b.processingDate + 'T12:00:00').getTime() -
                        new Date(b.hatchDate + 'T12:00:00').getTime()) /
                        86400000,
                    )
                  : null;
              const timeOnFarm = tofDays != null ? `${Math.floor(tofDays / 7)}w ${tofDays % 7}d` : null;
              return {
                starterLbs,
                growerLbs,
                totalFeed,
                mortality,
                mortalityPct,
                totalCost,
                perBird,
                feedPerBird,
                processingPerBird,
                totalMeat,
                timeOnFarm,
              };
            };

            // Processed-table cell formatters.
            const money0 = (n: number) => (n > 0 ? '$' + Math.round(n).toLocaleString() : '—');
            const money2 = (n: number) => (n > 0 ? '$' + n.toFixed(2) : '—');
            const lbs0 = (n: number) => (n > 0 ? Math.round(n).toLocaleString() + ' lbs' : '—');
            const wt2 = (v: number | string | null | undefined) =>
              parseFloat(String(v)) > 0 ? Number(v).toFixed(2) + ' lbs' : '—';
            const period = (b: BroilerBatchRow, which: 'brooder' | 'schooner') => {
              const live = calcTimeline(b.hatchDate, b.breed, b.processingDate, b.growoutWeeks, b.brooderDays);
              const inD =
                which === 'brooder'
                  ? b.brooderIn || (live ? live.brooderIn : null)
                  : live
                    ? live.schoonerIn
                    : b.schoonerIn;
              const outD =
                which === 'brooder'
                  ? b.brooderOut || (live ? live.brooderOut : null)
                  : live
                    ? live.schoonerOut
                    : b.schoonerOut;
              return fmtS(inD) + ' → ' + fmtS(outD);
            };

            const batchColumns: ProcessedColumn[] = [
              {
                key: 'name',
                label: 'Batch',
                primary: true,
                render: (b) => {
                  const C = getBatchDisplayColor(b);
                  return (
                    <span style={{display: 'inline-flex', alignItems: 'center', gap: 6}}>
                      <GroupDot color={C.bg} />
                      {b.name}
                    </span>
                  );
                },
              },
              {
                // One cell, two lines: breed over its hatchery (the mock's
                // "Breed · Hatchery"). Hatchery has no column of its own.
                key: 'breed',
                label: 'Breed · Hatchery',
                render: (b) => (
                  <span style={{display: 'inline-block'}}>
                    <StatusText tone="muted">{b.breed ? breedLabel(b.breed) : '—'}</StatusText>
                    <span style={{display: 'block', fontSize: 11, color: 'var(--label)'}}>{b.hatchery || '—'}</span>
                  </span>
                ),
              },
              {
                key: 'brooder',
                label: 'Brooder',
                mobilePriority: false,
                render: (b) => <StatusText tone="muted">{b.brooder || '—'}</StatusText>,
              },
              {
                key: 'schooner',
                label: 'Schooner',
                mobilePriority: false,
                render: (b) => <StatusText tone="muted">{b.schooner || '—'}</StatusText>,
              },
              {
                key: 'hatchDate',
                label: 'Hatch Date',
                mobilePriority: false,
                render: (b) => <StatusText tone="muted">{b.hatchDate ? fmt(b.hatchDate) : '—'}</StatusText>,
              },
              {
                key: 'brooderPeriod',
                label: 'Brooder Period',
                mobilePriority: false,
                render: (b) => <StatusText tone="muted">{period(b, 'brooder')}</StatusText>,
              },
              {
                key: 'schoonerPeriod',
                label: 'Schooner Period',
                mobilePriority: false,
                render: (b) => <StatusText tone="muted">{period(b, 'schooner')}</StatusText>,
              },
              {
                key: 'processingDate',
                label: 'Process Date',
                render: (b) => <StatusText tone="muted">{b.processingDate ? fmt(b.processingDate) : '—'}</StatusText>,
              },
              {
                key: 'timeOnFarm',
                label: 'Time on Farm',
                mobilePriority: false,
                render: (b) => <StatusText tone="muted">{statsFor(b).timeOnFarm || '—'}</StatusText>,
              },
              {
                key: 'birdCount',
                label: 'Birds Ordered',
                align: 'right',
                render: (b) => (b.birdCount || 0).toLocaleString(),
              },
              {
                key: 'birdsArrived',
                label: 'Birds Arrived',
                align: 'right',
                mobilePriority: false,
                render: (b) =>
                  parseInt(String(b.birdCountActual)) > 0 ? parseInt(String(b.birdCountActual)).toLocaleString() : '—',
              },
              {
                key: 'toProcessor',
                label: 'To Processor',
                align: 'right',
                render: (b) => (b.totalToProcessor || 0).toLocaleString(),
              },
              {
                key: 'mortality',
                label: 'Mortality',
                align: 'right',
                // Count over its percentage, as the mock pairs them. The
                // Mortality % column stays available on its own for anyone
                // who sorts or exports by the rate alone.
                render: (b) => {
                  const {mortality, mortalityPct} = statsFor(b);
                  const n = mortality.toLocaleString();
                  return (
                    <span style={{display: 'inline-block', textAlign: 'right'}}>
                      {mortality > 20 ? <StatusText tone="danger">{n}</StatusText> : n}
                      <span style={{display: 'block', fontSize: 11, color: 'var(--label)'}}>{mortalityPct}%</span>
                    </span>
                  );
                },
              },
              {
                key: 'mortalityPct',
                label: 'Mortality %',
                align: 'right',
                mobilePriority: false,
                render: (b) => statsFor(b).mortalityPct + '%',
              },
              {
                key: 'chickCost',
                label: 'Chick Cost',
                align: 'right',
                mobilePriority: false,
                render: (b) => money2(parseFloat(String(b.chickCost)) || 0),
              },
              {
                key: 'starterFeed',
                label: 'Starter Feed',
                align: 'right',
                mobilePriority: false,
                render: (b) => lbs0(statsFor(b).starterLbs),
              },
              {
                key: 'growerFeed',
                label: 'Grower Feed',
                align: 'right',
                mobilePriority: false,
                render: (b) => lbs0(statsFor(b).growerLbs),
              },
              {
                key: 'totalFeed',
                label: 'Total Feed',
                align: 'right',
                render: (b) => lbs0(statsFor(b).totalFeed),
              },
              {
                key: 'feedPerBird',
                label: 'Feed / Bird',
                align: 'right',
                mobilePriority: false,
                render: (b) => {
                  const v = statsFor(b).feedPerBird;
                  return v > 0 ? v.toFixed(2) + ' lbs' : '—';
                },
              },
              {
                key: 'week4Lbs',
                label: '4-wk Wt',
                align: 'right',
                mobilePriority: false,
                render: (b) => wt2(b.week4Lbs),
              },
              {
                key: 'week6Lbs',
                label: '6-wk Wt',
                align: 'right',
                mobilePriority: false,
                render: (b) => wt2(b.week6Lbs),
              },
              {
                key: 'avgBreast',
                label: 'Avg Breast',
                align: 'right',
                mobilePriority: false,
                render: (b) => wt2(b.avgBreastLbs),
              },
              {
                key: 'avgThigh',
                label: 'Avg Thigh',
                align: 'right',
                mobilePriority: false,
                render: (b) => wt2(b.avgThighsLbs),
              },
              {
                key: 'avgDressed',
                label: 'Avg Whole',
                align: 'right',
                mobilePriority: false,
                render: (b) => wt2(b.avgDressedLbs),
              },
              {
                key: 'totalMeat',
                label: 'Total Meat',
                align: 'right',
                mobilePriority: false,
                render: (b) => lbs0(statsFor(b).totalMeat),
              },
              {
                key: 'processingCost',
                label: 'Processing Cost',
                align: 'right',
                mobilePriority: false,
                render: (b) => money0(parseFloat(String(b.processingCost)) || 0),
              },
              {
                key: 'processingPerBird',
                label: 'Processing / Bird',
                align: 'right',
                mobilePriority: false,
                render: (b) => money2(statsFor(b).processingPerBird),
              },
              {
                key: 'totalCost',
                label: 'Total Cost',
                align: 'right',
                mobilePriority: false,
                render: (b) => money0(statsFor(b).totalCost),
              },
              {
                key: 'perBird',
                label: 'Cost / Bird',
                align: 'right',
                mobilePriority: false,
                render: (b) => money2(statsFor(b).perBird),
              },
              {
                // The row's OWN effective status, not a constant: one table
                // now carries all three lifecycle states.
                key: 'status',
                label: 'Status',
                render: (b) => <Badge status={processingStatusLabel(calcPoultryStatus(b))} />,
              },
            ];

            return (
              <div>
                {cardRows.length === 0 ? (
                  <div
                    style={{
                      padding: '2.5rem',
                      textAlign: 'center',
                      color: 'var(--ink-faint)',
                      ...S.card,
                    }}
                    data-broiler-batches-empty={totalCount === 0 ? 'true' : 'filtered'}
                  >
                    No broiler batches match the current filters
                  </div>
                ) : (
                  <div style={S.card}>
                    <DataTable
                      surfaceKey="broiler-batches"
                      rows={cardRows}
                      rowKey="id"
                      density="compact"
                      card={{
                        main: (batch) => batch.name,
                        // The phone card reads for a planned batch too, so it
                        // leads with hatch date rather than a processed date
                        // that may not exist yet.
                        line1: (batch) => `${breedLabel(batch.breed) || '—'} · hatch ${fmt(batch.hatchDate) || '—'}`,
                        line2: (batch) => {
                          const stats = statsFor(batch);
                          return `${stats.mortality.toLocaleString()} mort. · ${Math.round(stats.totalFeed).toLocaleString()} lb feed`;
                        },
                        badge: (batch) => <Badge status={processingStatusLabel(calcPoultryStatus(batch))} />,
                      }}
                      columns={batchColumns.filter((c) => columnVisible(c.key))}
                      onRowOpen={(b) => openBatch(b, cardRows)}
                    />
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

function BroilerListRouter(props: BroilerListProps) {
  const location = useLocation();
  if (location.pathname.startsWith('/broiler/batches/')) {
    return React.createElement(BroilerBatchPage, props);
  }
  return React.createElement(BroilerListHub, props);
}

export default BroilerListRouter;
